import React, { useState, useEffect, useMemo } from 'react';
import chemicalService from '../../../services/chemicalService';

const UNIT_OPTIONS = ['Kg', 'Liter', 'Gram', 'ML', 'Piece', 'Meter'];
const PRODUCT_TYPES = [
  { id: 'finished', label: 'Finished' },
  { id: 'raw', label: 'Raw' },
  { id: 'trading', label: 'Trading' },
];

const blankIngredient = () => ({
  product_id: '',
  quantity: '',
  unit: 'Kg',
});

const FormulaFormModal = ({ formula, onClose, onSaved }) => {
  const isEdit = Boolean(formula);

  const [allProducts, setAllProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [outputType, setOutputType] = useState('finished');

  // Products matching selected output type
  const finishedProducts = useMemo(
    () => allProducts.filter((p) => p.type === outputType),
    [allProducts, outputType]
  );

  const [form, setForm] = useState({
    code: '',
    product_id: '',
    customer_id: '',
    yield_qty: '',
    total_input_qty: '',
    autoSyncTotal: true,
    notes: '',
    version: 1,
    is_active: true,
    ingredients: [blankIngredient()],
  });
  const [submitting, setSubmitting] = useState(false);

  // Load products + customers on mount
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingProducts(true);
        const [allRes, custRes] = await Promise.all([
          chemicalService.getProducts({ page: 1, limit: 1000 }),
          chemicalService.getCustomers({ page: 1, limit: 1000 }),
        ]);
        setAllProducts(allRes.data || []);
        setCustomers((custRes.data || []).filter((c) => c.is_active));
      } catch (err) {
        console.error('Error loading data:', err);
        alert('❌ Failed to load products or customers');
      } finally {
        setLoadingProducts(false);
      }
    };
    load();
  }, []);

  // Hydrate form when editing
  useEffect(() => {
    if (formula) {
      setForm({
        code: formula.code || '',
        product_id: formula.product_id || '',
        customer_id: formula.customer_id ? String(formula.customer_id) : '',
        yield_qty: formula.yield_qty != null ? formula.yield_qty : '',
        total_input_qty: formula.total_input_qty != null ? formula.total_input_qty : '',
        autoSyncTotal: false,
        notes: formula.notes || '',
        version: formula.version || 1,
        is_active: formula.is_active !== undefined ? formula.is_active : true,
        ingredients: (formula.ingredients && formula.ingredients.length > 0)
          ? formula.ingredients.map((ing) => ({
              product_id: ing.product_id,
              quantity: ing.quantity != null ? ing.quantity : '',
              unit: ing.unit || 'Kg',
            }))
          : [blankIngredient()],
      });
    }
  }, [formula]);

  // On edit: auto-select the product type of the recipe's output product
  useEffect(() => {
    if (formula && formula.product_id && allProducts.length > 0) {
      const match = allProducts.find((p) => String(p.id) === String(formula.product_id));
      if (match && match.type) setOutputType(match.type);
    }
  }, [formula, allProducts]);

  const ingredientsTotal = useMemo(() => {
    return form.ingredients.reduce((sum, ing) => {
      const q = parseFloat(ing.quantity);
      return sum + (isNaN(q) ? 0 : q);
    }, 0);
  }, [form.ingredients]);

  // Auto-sync total_input_qty when autoSync is on
  useEffect(() => {
    if (form.autoSyncTotal) {
      setForm((prev) => ({
        ...prev,
        total_input_qty: ingredientsTotal > 0 ? ingredientsTotal.toFixed(2).replace(/\.?0+$/, '') : '',
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingredientsTotal, form.autoSyncTotal]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleIngredientChange = (index, field, value) => {
    setForm((prev) => {
      const next = [...prev.ingredients];
      next[index] = { ...next[index], [field]: value };
      if (field === 'product_id' && value) {
        const product = allProducts.find((p) => p.id === parseInt(value, 10));
        if (product?.base_unit) {
          next[index].unit = product.base_unit;
        }
      }
      return { ...prev, ingredients: next };
    });
  };

  const addIngredient = () => {
    setForm((prev) => ({ ...prev, ingredients: [...prev.ingredients, blankIngredient()] }));
  };

  const removeIngredient = (index) => {
    if (form.ingredients.length === 1) {
      alert('❌ A Recipe needs at least one ingredient.');
      return;
    }
    setForm((prev) => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.code.trim() || !form.product_id || !form.yield_qty || !form.total_input_qty) {
      alert('❌ Code, Product, Yield, and Total Input are required.');
      return;
    }
    if (parseFloat(form.yield_qty) <= 0) {
      alert('❌ Yield quantity must be greater than 0.');
      return;
    }

    for (let i = 0; i < form.ingredients.length; i++) {
      const ing = form.ingredients[i];
      if (!ing.product_id || !ing.quantity || !ing.unit) {
        alert(`❌ Ingredient ${i + 1}: product, quantity, and unit are required.`);
        return;
      }
      if (parseFloat(ing.quantity) <= 0) {
        alert(`❌ Ingredient ${i + 1}: quantity must be greater than 0.`);
        return;
      }
    }

    const productIds = form.ingredients.map((ing) => parseInt(ing.product_id, 10));
    if (new Set(productIds).size !== productIds.length) {
      alert('❌ Each ingredient must be a different product. Combine duplicate entries.');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        code: form.code.trim(),
        product_id: parseInt(form.product_id, 10),
        customer_id: form.customer_id ? parseInt(form.customer_id, 10) : null,
        yield_qty: parseFloat(form.yield_qty),
        total_input_qty: parseFloat(form.total_input_qty),
        notes: form.notes.trim() || null,
        version: parseInt(form.version, 10) || 1,
        is_active: form.is_active,
        ingredients: form.ingredients.map((ing, i) => ({
          product_id: parseInt(ing.product_id, 10),
          quantity: parseFloat(ing.quantity),
          unit: ing.unit,
          display_order: i,
        })),
      };

      if (isEdit) {
        await chemicalService.updateFormula(formula.id, payload);
        alert('✅ Recipe updated successfully!');
      } else {
        await chemicalService.createFormula(payload);
        alert('✅ Recipe created successfully!');
      }
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save recipe';
      alert('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  const getIngredientProduct = (productId) => {
    return allProducts.find((p) => p.id === parseInt(productId, 10));
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>
              {isEdit ? '✏️ Edit Recipe' : '➕ New Recipe'}
            </h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              {isEdit ? `Updating ${formula.code}` : 'Define a manufacturing recipe'}
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px 30px' }}>

            {/* ─── Identification ─────── */}
            <div style={styles.sectionLabel}>Identification</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Recipe Code <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => handleChange('code', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. R-007"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Product Type <span style={styles.required}>*</span>
                </label>
                <select
                  value={outputType}
                  onChange={(e) => {
                    setOutputType(e.target.value);
                    handleChange('product_id', '');
                  }}
                  style={styles.input}
                  disabled={loadingProducts}
                >
                  {PRODUCT_TYPES.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ ...styles.formGroup, flex: 2 }}>
                <label style={styles.label}>
                  Output Product <span style={styles.required}>*</span>
                </label>
                <select
                  value={form.product_id}
                  onChange={(e) => handleChange('product_id', e.target.value)}
                  style={styles.input}
                  required
                  disabled={loadingProducts}
                >
                  <option value="">
                    {loadingProducts
                      ? 'Loading products...'
                      : finishedProducts.length === 0
                        ? `-- No ${outputType} products available --`
                        : '-- Select product --'}
                  </option>
                  {finishedProducts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.code} — {p.name}
                    </option>
                  ))}
                </select>
                {!loadingProducts && finishedProducts.length === 0 && (
                  <span style={styles.hint}>
                    Create a product with type "{outputType}" first.
                  </span>
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Version</label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={form.version}
                  onChange={(e) => handleChange('version', e.target.value)}
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.row}>
              <div style={{ ...styles.formGroup, flex: 2 }}>
                <label style={styles.label}>Customer (optional)</label>
                <select
                  value={form.customer_id}
                  onChange={(e) => handleChange('customer_id', e.target.value)}
                  style={styles.input}
                  disabled={loadingProducts}
                >
                  <option value="">— Generic (no customer) —</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <span style={styles.hint}>Pick a customer if this recipe is made for a specific one.</span>
              </div>
            </div>

            {/* ─── Output & Input ─────── */}
            <div style={styles.sectionLabel}>Output & Input</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Yield Quantity <span style={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.yield_qty}
                  onChange={(e) => handleChange('yield_qty', e.target.value)}
                  style={styles.input}
                  placeholder="Output per batch"
                  required
                />
                <span style={styles.hint}>How much finished product comes out per run</span>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Total Input Quantity <span style={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.total_input_qty}
                  onChange={(e) => {
                    handleChange('total_input_qty', e.target.value);
                    handleChange('autoSyncTotal', false);
                  }}
                  style={styles.input}
                  placeholder="Sum of raw materials"
                  required
                />
                <label style={styles.autoSyncToggle}>
                  <input
                    type="checkbox"
                    checked={form.autoSyncTotal}
                    onChange={(e) => handleChange('autoSyncTotal', e.target.checked)}
                  />
                  <span>Auto-sync from ingredients</span>
                </label>
              </div>
            </div>

            {form.yield_qty && form.total_input_qty && parseFloat(form.total_input_qty) > 0 && (
              <div style={styles.yieldPreview}>
                Yield ratio: <strong>
                  {((parseFloat(form.yield_qty) / parseFloat(form.total_input_qty)) * 100).toFixed(1)}%
                </strong>
                <span style={{ color: '#7f8c8d', fontSize: '12px', marginLeft: '8px' }}>
                  ({form.yield_qty} from {form.total_input_qty} input)
                </span>
              </div>
            )}

            {/* ─── Ingredients ─────────── */}
            <div style={styles.sectionLabel}>
              Ingredients ({form.ingredients.length})
            </div>

            <div style={styles.ingredientsList}>
              {form.ingredients.map((ing, index) => {
                const qty = parseFloat(ing.quantity) || 0;
                const pctOfTotal = ingredientsTotal > 0 ? (qty / ingredientsTotal) * 100 : 0;

                return (
                  <div key={index} style={styles.ingredientRow}>
                    <div style={styles.ingredientIndex}>{index + 1}</div>
                    <div style={{ flex: 3, minWidth: '200px' }}>
                      <label style={styles.miniLabel}>Raw material</label>
                      <select
                        value={ing.product_id}
                        onChange={(e) => handleIngredientChange(index, 'product_id', e.target.value)}
                        style={styles.input}
                        required
                        disabled={loadingProducts}
                      >
                        <option value="">-- Select product --</option>
                        {allProducts.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.code} — {p.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: 1, minWidth: '110px' }}>
                      <label style={styles.miniLabel}>Quantity</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={ing.quantity}
                        onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
                        style={styles.input}
                        placeholder="0"
                        required
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: '90px' }}>
                      <label style={styles.miniLabel}>Unit</label>
                      <select
                        value={ing.unit}
                        onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)}
                        style={styles.input}
                        required
                      >
                        {UNIT_OPTIONS.map((u) => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ minWidth: '70px', textAlign: 'right' }}>
                      <label style={styles.miniLabel}>% of mix</label>
                      <div style={styles.pctDisplay}>
                        {ingredientsTotal > 0 ? `${pctOfTotal.toFixed(1)}%` : '—'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeIngredient(index)}
                      style={styles.removeButton}
                      title="Remove ingredient"
                      disabled={form.ingredients.length === 1}
                    >
                      🗑️
                    </button>
                  </div>
                );
              })}
            </div>

            <button type="button" onClick={addIngredient} style={styles.addIngredientButton}>
              + Add ingredient
            </button>

            <div style={styles.ingredientsTotalRow}>
              <span>Ingredients sum:</span>
              <strong>
                {ingredientsTotal > 0 ? ingredientsTotal.toFixed(2).replace(/\.?0+$/, '') : '0'}
                {form.ingredients[0]?.unit ? ` ${form.ingredients[0].unit}` : ''}
              </strong>
            </div>

            {/* ─── Notes ─────── */}
            <div style={styles.sectionLabel}>Notes</div>
            <div style={styles.formGroup}>
              <textarea
                value={form.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                style={{ ...styles.input, minHeight: '70px', resize: 'vertical', fontFamily: 'inherit' }}
                placeholder="e.g. Mix at 60–70°C for 45 minutes. Yield is 95% due to evaporation."
                rows="3"
              />
            </div>

            <div style={{ ...styles.formGroup, marginTop: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>
                  Active (uncheck to retire this Recipe)
                </span>
              </label>
            </div>

          </div>

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" style={styles.submitButton} disabled={submitting}>
              {submitting ? '⏳ Saving...' : (isEdit ? '💾 Update Recipe' : '💾 Create Recipe')}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

const styles = {
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    background: 'white', borderRadius: '12px',
    width: '95%', maxWidth: '960px', maxHeight: '92vh',
    overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '20px 30px', borderBottom: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    position: 'sticky', top: 0, background: 'white', zIndex: 10,
  },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: {
    padding: '8px 16px', background: '#f0f0f0', border: 'none',
    borderRadius: '8px', cursor: 'pointer', fontSize: '20px', fontWeight: '700', color: '#666',
  },
  sectionLabel: {
    fontSize: '12px', fontWeight: '700', color: '#7f8c8d',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    marginTop: '20px', marginBottom: '12px',
    paddingBottom: '6px', borderBottom: '1px solid #f0f0f0',
  },
  row: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  formGroup: {
    flex: 1, minWidth: '180px', display: 'flex', flexDirection: 'column',
    gap: '6px', marginBottom: '12px',
  },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  miniLabel: {
    fontSize: '11px', fontWeight: '600', color: '#7f8c8d',
    marginBottom: '4px', display: 'block',
    textTransform: 'uppercase', letterSpacing: '0.3px',
  },
  required: { color: '#e74c3c' },
  input: {
    width: '100%', padding: '10px 12px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit',
  },
  hint: { fontSize: '11px', color: '#95a5a6', fontStyle: 'italic', marginTop: '2px' },
  autoSyncToggle: {
    display: 'flex', alignItems: 'center', gap: '6px',
    fontSize: '12px', color: '#7f8c8d', cursor: 'pointer', marginTop: '4px',
  },
  yieldPreview: {
    background: '#e8f5e9', borderRadius: '8px',
    padding: '10px 14px', marginBottom: '12px',
    color: '#155724', fontSize: '14px',
  },
  ingredientsList: {
    display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px',
  },
  ingredientRow: {
    display: 'flex', gap: '10px', alignItems: 'flex-end',
    padding: '12px', background: '#f8f9fa', borderRadius: '10px', flexWrap: 'wrap',
  },
  ingredientIndex: {
    width: '28px', height: '28px', borderRadius: '50%',
    background: '#e74c3c', color: 'white',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '12px', fontWeight: '700', flexShrink: 0, marginBottom: '10px',
  },
  pctDisplay: {
    padding: '10px 0', fontSize: '14px', fontWeight: '700',
    color: '#2c3e50', textAlign: 'right',
  },
  removeButton: {
    padding: '10px 12px', background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'pointer',
    fontSize: '14px', height: '42px', flexShrink: 0,
  },
  addIngredientButton: {
    width: '100%', padding: '12px',
    background: 'white', color: '#e74c3c',
    border: '2px dashed #e74c3c', borderRadius: '10px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '700', marginBottom: '12px',
  },
  ingredientsTotalRow: {
    display: 'flex', justifyContent: 'space-between',
    padding: '12px 16px', background: '#fffbea', borderRadius: '10px',
    fontSize: '14px', color: '#856404', border: '1px solid #f9e0a0',
  },
  modalFooter: {
    padding: '16px 30px', borderTop: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'flex-end', gap: '12px',
    position: 'sticky', bottom: 0, background: 'white',
  },
  cancelButton: {
    padding: '12px 24px', background: '#95a5a6', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'pointer',
    fontSize: '14px', fontWeight: '600',
  },
  submitButton: {
    padding: '12px 28px', background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'pointer',
    fontSize: '14px', fontWeight: '600',
  },
};

export default FormulaFormModal;